import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, EmptyState, Icon, Tooltip } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useTwoFAContext } from 'hooks/useTwoFAContext'

interface BeneficiaryEmptyStateProps {
  onAddBeneficiary: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

export const BeneficiaryEmptyState = ({ onAddBeneficiary }: BeneficiaryEmptyStateProps) => {
  const { t } = useTranslation()
  const { enableLegacyBeneficiaryCreation } = useFeatureFlags()
  const { twoFactorEnabled: isTwoFactorEnabled } = useTwoFAContext() || {}
  const permissions = useMappedRoles()
  const canEdit = permissions?.manageBeneficiaries?.canEdit

  const EmptyStateDescription = () => {
    if (canEdit && enableLegacyBeneficiaryCreation) {
      return (
        <span className="text-foreground-prominent text-body-regular">
          {isTwoFactorEnabled
            ? t('manageBeneficiaries.noBeneficiariesDescription')
            : t('manageBeneficiaries.noBeneficiariesNo2FADescription')}
        </span>
      )
    } else {
      return (
        <span className="text-foreground-prominent text-body-regular">
          {t('manageBeneficiaries.noBeneficiariesAndCannotAdd')}
        </span>
      )
    }
  }

  const AddBeneficiaryButton = useMemo(() => {
    if (!canEdit || !enableLegacyBeneficiaryCreation) return <></>
    return (
      <Button
        iconElement={<Icon name="Plus" />}
        onClick={onAddBeneficiary}
        disabled={!isTwoFactorEnabled}
      >
        {t('manageBeneficiaries.addBeneficiary')}
      </Button>
    )
  }, [isTwoFactorEnabled, canEdit, enableLegacyBeneficiaryCreation, onAddBeneficiary])

  return (
    <EmptyState
      testId="empty-state"
      LottiePlayer={<Lottie src="no-data" />}
      title={t('manageBeneficiaries.noBeneficiaries')}
      description={<EmptyStateDescription />}
      PrimaryButton={
        isTwoFactorEnabled ? (
          AddBeneficiaryButton
        ) : (
          <Tooltip
            bodyContent={`${t('manageBeneficiaries.noBeneficiariesNo2FALink')}`}
            type="complex"
          >
            {AddBeneficiaryButton}
          </Tooltip>
        )
      }
    />
  )
}
